import dayjs from 'dayjs';

interface FilterLabelMap {
  from_location: string;
  to_location: string;
  status: string;
  [key: string]: string;
}

const filterLabelMap: FilterLabelMap = {
  from_location: 'Origin',
  to_location: 'Destination',
  status: 'Status',
  scheduled_pickup_date: 'Pickup date',
  scheduled_delivery_date: 'Delivery date',
  customer_defined_ship_date: 'Customer defined ship date',
  created_at: 'Created date',
  actual_pickup_date: 'Actual pickup date',
  has_comments: 'Has comments',
  unread_mentions: 'Unread mentions',
  shipper_rtg: 'Shipper RTG',
  consignee_rtg: 'Consignee RTG',
};

/**
 * Converts a filter key to a human-readable label
 *
 * @param fb - Filter key to convert
 * @returns Human-readable label string
 */
export const toLabel = (fb: unknown): string =>
  filterLabelMap[fb as string] ||
  (typeof fb === 'string'
    ? fb.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase())
    : 'Unknown');

interface BaseMenuStyle {
  width: string;
  borderRadius: string;
  boxShadow: string;
  marginTop: string;
  paddingTop: string;
  paddingBottom: string;
  maxHeight: string;
  overflowY: React.CSSProperties['overflowY'];
}

export const SUBFILTER_MENU_MAX_HEIGHT_CSS = 'min(calc(100dvh - 200px), 550px)';
export const SUBFILTER_MENU_MAX_HEIGHT_PX = 550;
/** Keeps the submenu from collapsing while async options load. */
export const SUBFILTER_LOADING_MIN_HEIGHT = 240;

export const SUBFILTER_FOOTER_HEIGHT = 56;
export const SUBFILTER_HEADER_EST = 44;
export const SUBFILTER_SEARCH_EST = 48;

export const baseMenuStyle: BaseMenuStyle = {
  width: '296px',
  borderRadius: '8px',
  boxShadow: '0px 4px 16px 0px rgba(0, 0, 0, 0.27)',
  marginTop: '5px',
  paddingTop: '8px',
  maxHeight: 'min(calc(100dvh - 200px), 550px)',
  overflowY: 'auto',
  paddingBottom: '8px',
};

/**
 * Creates a JSON string ID from start and end dates
 *
 * @param start - Start date string or null
 * @param end - End date string or null
 * @returns JSON string representation of date range
 */
export const makeDateId = (
  start?: string | null,
  end?: string | null
): string => JSON.stringify({ start: start ?? null, end: end ?? null });

/**
 * Builds a human-readable date label for filters
 *
 * @param key - Date filter key (scheduled_pickup_date or scheduled_delivery_date)
 * @param start - Start date string
 * @param end - End date string
 * @returns Formatted date label
 */
export function buildDateLabel(
  key: string,
  start: string | null,
  end: string | null,
  dateFormat?: string | null
): string {
  const format = (d: string | null) => {
    if (!d) return '';
    const parsed = dayjs(d);
    return parsed.isValid()
      ? parsed.format((dateFormat || 'MM/DD/YYYY').replace(/\//g, '-'))
      : d;
  };

  switch (key) {
    case 'scheduled_pickup_date':
      return `Pickup date: ${format(start)} - ${format(end)}`;

    case 'scheduled_delivery_date':
      return `Delivery date: ${format(start)} - ${format(end)}`;

    case 'customer_defined_ship_date':
      return `Customer defined ship date: ${format(start)} - ${format(end)}`;

    case 'actual_pickup_date':
      return `Actual pickup date: ${format(start)} - ${format(end)}`;

    case 'created_at':
      return `Created date: ${format(start)} - ${format(end)}`;

    default: {
      const label =
        key?.replace(/_/g, ' ')?.replace(/\b\w/g, (c) => c.toUpperCase()) ||
        'Date';
      return `${label}: ${format(start)} - ${format(end)}`;
    }
  }
}

interface FilterItem {
  filterBy: string;
  filterValue: string[];
}

/**
 * Replaces a value in a filter array, adding the filter if it doesn't exist
 *
 * @param filterArray - Array of filter items
 * @param key - Filter key to update
 * @param oldId - Value to replace
 * @param newId - New value to add
 * @returns Updated filter array
 */
export const replaceInFilterArray = (
  filterArray: FilterItem[] | null | undefined,
  key: string,
  oldId: string | number,
  newId: string | number
): FilterItem[] => {
  const prev = Array.isArray(filterArray)
    ? JSON.parse(JSON.stringify(filterArray))
    : [];
  const i = prev.findIndex((x: FilterItem) => x?.filterBy === key);
  if (i === -1) return [...prev, { filterBy: key, filterValue: [newId] }];

  const before = Array.isArray(prev[i].filterValue) ? prev[i].filterValue : [];
  const after = Array.from(
    new Set(
      before
        .filter((v: string | number) => String(v) !== String(oldId))
        .concat([newId])
    )
  );
  prev[i] = { ...prev[i], filterValue: after };
  return prev;
};

/**
 * Removes a value from a filter array, removing the entire filter if no values remain
 *
 * @param filterArray - Array of filter items
 * @param key - Filter key to update
 * @param oldId - Value to remove
 * @returns Updated filter array
 */
export const removeFromFilterArray = (
  filterArray: FilterItem[] | null | undefined,
  key: string,
  oldId: string | number
): FilterItem[] => {
  const prev = Array.isArray(filterArray)
    ? JSON.parse(JSON.stringify(filterArray))
    : [];
  const i = prev.findIndex((x: FilterItem) => x?.filterBy === key);
  if (i === -1) return prev;
  const after = (prev[i].filterValue || []).filter(
    (v: string | number) => String(v) !== String(oldId)
  );
  if (after.length === 0)
    return prev.filter((_: FilterItem, idx: number) => idx !== i);
  prev[i] = { ...prev[i], filterValue: after };
  return prev;
};
